'use client';

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import Iconography from '../Iconography/Iconography';
import Tooltip from '../Tooltip/Tooltip';
import Typography from '../Typography/Typography';
import Box from '../Box/Box';

import styles from './Checkbox.module.scss';

type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type ValidationType = 'success' | 'warning' | 'error';

interface ValidationResponse {
  validationType: ValidationType;
  message: string;
}

interface RegisterFunction {
  (name: string): {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
}

export interface CheckboxProps {
  name?: string;
  register?: RegisterFunction;
  theme?: Theme;
  label?: string;
  direction?: Direction;
  isChecked?: boolean;
  isDisabled?: boolean;
  handleChange?: (checked: boolean) => void;
  dataTestId: string;
  gaComponent: string;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * Set to false when using inside other components like Dropdown.
   * @default true
   */
  showValidation?: boolean;
  /**
   * Tooltip text displayed via an info icon next to the label.
   * When provided, an (i) icon appears after the label text.
   */
  tooltipText?: string;
  logEvents?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  name,
  register,
  theme = 'dark',
  label,
  direction = 'ltr',
  isChecked = false,
  isDisabled = false,
  handleChange = () => {},
  dataTestId,
  gaComponent,
  validationResponse,
  showValidation = true,
  tooltipText,
  logEvents = true,
}) => {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    e.preventDefault();

    if (!isDisabled) {
      setChecked(!checked);
      handleChange(!checked);
    }

    if (typeof window !== 'undefined' && logEvents) {
      const dataLayerMap: Record<string, unknown> = {
        event: 'interaction',
        interaction__type: 'checkbox',
        interaction__component: gaComponent,
      };
      if (label) {
        dataLayerMap.interaction__text = label;
      }

      (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
    }
  };

  return (
    <Box>
      <div
        className={classnames(styles.checkBox, styles[`theme_${theme}`], {
          [styles.disabled]: isDisabled,
        })}
        data-testid={dataTestId}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleClick(e);
          }
        }}
        tabIndex={0}
        role="checkbox"
        aria-checked={checked}
        aria-disabled={isDisabled}
      >
        <div
          className={classnames(
            styles.checkBoxIconWrapper,
            styles[`validation-${validationResponse?.validationType}`],
            { [styles.checked]: checked }
          )}
        >
          <input
            id={dataTestId}
            className={styles.hiddenInput}
            checked={checked}
            disabled={isDisabled}
            type="checkbox"
            {...(typeof register === 'function' && name ? register(name) : { name })}
            readOnly
          />
          {checked && (
            <Iconography
              category="global"
              color={
                theme === 'light'
                  ? isDisabled
                    ? 'gray-5'
                    : 'white'
                  : isDisabled
                    ? 'gray-5'
                    : 'black'
              }
              name="checkmark"
              size={20}
            />
          )}
        </div>
        {label && (
          <label htmlFor={dataTestId} className={styles.labelWrapper}>
            <Typography className={styles.label} variant="body" size="small" direction={direction}>
              {label}
            </Typography>
            {tooltipText && (
              <Tooltip
                content={tooltipText}
                side="top"
                theme={theme}
                dataTestId={`${dataTestId}-tooltip`}
              >
                <span
                  className={styles.tooltipIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  role="img"
                  aria-label="More information"
                >
                  <Iconography
                    category="global"
                    name="information"
                    color={isDisabled ? 'gray-5' : theme === 'dark' ? 'white' : 'black'}
                  />
                </span>
              </Tooltip>
            )}
          </label>
        )}
      </div>
      {showValidation && (
        <Box
          mt={4}
          className={classnames(
            styles.validationMessageWrapper,
            styles[theme],
            validationResponse?.validationType &&
              styles[`validation-${validationResponse.validationType}`]
          )}
          role="alert"
          aria-live="assertive"
        >
          {validationResponse?.message && (
            <Typography
              className={styles.validationMessage}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              id={`${name}-validation-message`}
            >
              {validationResponse.message}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
